/**
 * PM - Prefetch en background del estado "Agotados" contrario (hotfix
 * 2026-09-14).
 *
 * Objetivo principal: cuando el usuario marca/desmarca "Agotados",
 * assets/facets.js (sin modificar) ya tiene una cache nativa --
 * FacetFiltersForm.filterData, un array de { html, url } -- y usa
 * renderSectionFromCache() en vez de hacer fetch si la URL exacta ya está
 * ahí (ver su método renderPage()). Este archivo aprovecha esa cache:
 * mientras la página está idle (nunca compitiendo con el first paint),
 * precalcula la URL EXACTA que facets.js usaría para el estado contrario de
 * disponibilidad y la deja lista en FacetFiltersForm.filterData -- sin
 * renderizar nada, sin tocar assets/facets.js, sin crear un segundo motor
 * de facets.
 *
 * Objetivo secundario (hotfix 2026-09-14): ese mismo HTML precalculado
 * resuelve GRATIS el count de Agotados cuando pm-collection-filter-
 * availability.liquid no pudo calcularlo server-side (Agotados OFF + algún
 * otro filtro activo -- ver ese snippet). El estado contrario que este
 * archivo ya descarga es exactamente "Agotados ON con los mismos otros
 * filtros", y ESE estado siempre conoce el número real (resta aritmética
 * nativa, nunca queda pendiente) -- fillPendingAgotadosCount() solo lee ese
 * HTML ya obtenido y actualiza el span pendiente de la página actual. Cero
 * fetch adicional exclusivo para Agotados.
 *
 * CONSTRUCCIÓN DE LA URL -- por qué debe ser BYTE A BYTE igual a la que
 * facets.js generaría: FacetFiltersForm busca por igualdad de string
 * exacta (`element.url === url`), no por comparación semántica de query
 * params. facets.js construye esa URL así (ver su onSubmitHandler /
 * getSections):
 *   `${location.pathname}?section_id=${sectionId}&${searchParams}`
 *   sectionId = document.querySelector('#product-grid, #product-rows').dataset.id
 *   searchParams = new URLSearchParams(new FormData(#FacetsFilterForm)).toString()
 * searchParams sale de FormData del formulario real -- no de
 * window.location.search -- porque el input oculto de disponibilidad
 * (data-pm-availability-native, form="FacetsFilterForm") vive fuera del
 * <form> físico y solo FormData lo recoge en su posición real. Para
 * obtener el string EXACTO del estado contrario sin esperar a que el
 * usuario haga click, este archivo invierte momentáneamente el .checked de
 * ese input (sin disparar 'change', sin que ningún listener reaccione),
 * lee el FormData resultante, y lo revierte de inmediato -- mismo mecanismo
 * que usaría el navegador si el usuario hiciera ese click, solo que sin
 * pedirle que lo haga.
 *
 * CUÁNDO CORRE: solo dentro de <pm-facets-prefetch-scope>, el mismo patrón
 * de custom element que <pm-collection-counts-scope> -- vive en el sidebar
 * de filtros (pm-collection-filter-availability.liquid) y su
 * connectedCallback() se re-dispara en cada respuesta AJAX de facets.js
 * (renderFilters() reinserta ese sidebar), así que cada cambio de
 * Marca/Tipo/Precio recalcula el estado contrario para la combinación
 * ACTUAL. Timing (hotfix 2026-09-14, corregido tras una regresión): el
 * disparo real espera a window.load (documento + CSS + imágenes ya
 * cargados) más 250ms fijos -- nunca compite con el first paint real, ni
 * siquiera en una página lenta donde el grid tarda en terminar de
 * estilizarse tras el H1.
 *
 * DEDUPE: antes de cada fetch se comprueba FacetFiltersForm.filterData (no
 * repetir una URL ya cacheada) y un Set local de URLs en vuelo (no lanzar
 * dos fetch idénticos en paralelo). No se usa AbortController aquí a
 * propósito: esto es un prefetch especulativo de bajo riesgo -- si el
 * usuario cambia de filtro antes de que resuelva, el resultado igual
 * termina en cache (para esa combinación ya obsoleta) sin causar ningún
 * efecto visible; no hay nada que cancelar por seguridad de UI, a
 * diferencia de assets/pm-collection-counts.js, que sí escribe directamente
 * un número en pantalla y por eso sí necesita abortar respuestas viejas.
 *
 * Data Saver: si navigator.connection.saveData es true, no se prefetch-ea
 * nada -- el storefront sigue funcionando normal con el fetch nativo de
 * facets.js al click real.
 */
(function () {
  const inFlight = new Set();

  // PM - hotfix 2026-09-14 (corrige regresión): el doble requestAnimationFrame
  // podía dispararse mientras el H1 ya pintó pero el grid todavía está
  // terminando de recibir estilos/imágenes -- Alejandro confirmó que eso
  // seguía compitiendo con la carga real. Se reemplaza por window.load: el
  // prefetch de Agotados espera a que la página esté COMPLETAMENTE cargada
  // (documento + CSS + imágenes) más un margen fijo de 250ms, así jamás
  // compite con el first paint real. Si window.load ya ocurrió para cuando
  // este script corre (documento pesado, conexión lenta), se dispara de
  // inmediato en vez de esperar un evento que ya pasó.
  function runAfterPageLoad(callback) {
    if (document.readyState === 'complete') {
      window.setTimeout(callback, 250);
      return;
    }

    window.addEventListener(
      'load',
      () => {
        window.setTimeout(callback, 250);
      },
      { once: true },
    );
  }

  function isSaveDataOn() {
    return Boolean(window.navigator.connection && window.navigator.connection.saveData);
  }

  function getSectionId() {
    const grid = document.querySelector('#product-grid, #product-rows');
    return grid ? grid.dataset.id : null;
  }

  function buildAlternateSearchParams() {
    const form = document.getElementById('FacetsFilterForm');
    const nativeInput = document.querySelector('[data-pm-availability-native]');
    if (!form || !nativeInput) {
      return null;
    }

    const original = nativeInput.checked;
    nativeInput.checked = !original;
    const params = new URLSearchParams(new FormData(form)).toString();
    nativeInput.checked = original;

    return params;
  }

  function buildAlternateUrl() {
    const sectionId = getSectionId();
    if (!sectionId) {
      return null;
    }

    const params = buildAlternateSearchParams();
    if (params === null) {
      return null;
    }

    return `${window.location.pathname}?section_id=${sectionId}&${params}`;
  }

  function getCachedEntry(url) {
    if (typeof FacetFiltersForm === 'undefined' || !Array.isArray(FacetFiltersForm.filterData)) {
      return null;
    }
    return FacetFiltersForm.filterData.find((entry) => entry.url === url) || null;
  }

  // PM - hotfix 2026-09-14 (Agotados count no negociable): cuando el count
  // de Agotados no puede resolverse server-side (Agotados OFF + algún otro
  // filtro activo -- ver pm-collection-filter-availability.liquid), ese
  // span queda visible con un placeholder ("…") y dos marcadores:
  // data-pm-agotado-count (valor actual, vacío mientras pendiente) y
  // data-pm-agotado-pending. El HTML que este mismo archivo ya descarga
  // para calentar la cache de Agotados (estado CONTRARIO -- si hoy está
  // OFF, el contrario es ON) SIEMPRE conoce el número real de Agotados en
  // ese otro estado (ver el snippet: Agotados ON se resuelve con una resta
  // aritmética nativa, nunca queda pendiente) -- así que no hace falta un
  // fetch nuevo exclusivo para esto, solo leer ese mismo HTML.
  function fillPendingAgotadosCount(html) {
    const pendingNode = document.querySelector('[data-pm-agotado-count][data-pm-agotado-pending]');
    if (!pendingNode) {
      return;
    }

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const alternateNode = parsed.querySelector('[data-pm-agotado-count]');
    if (!alternateNode) {
      return;
    }

    const value = alternateNode.getAttribute('data-pm-agotado-count');
    if (value === null || value === '') {
      return;
    }

    pendingNode.textContent = value;
    pendingNode.setAttribute('data-pm-agotado-count', value);
    pendingNode.setAttribute('data-pm-agotados-global', value);
    pendingNode.removeAttribute('data-pm-agotado-pending');

    document.dispatchEvent(new CustomEvent('pm:availability-counts-updated'));
  }

  function prefetchAlternateAvailability() {
    if (isSaveDataOn()) {
      return;
    }
    if (typeof FacetFiltersForm === 'undefined' || !Array.isArray(FacetFiltersForm.filterData)) {
      return;
    }

    const url = buildAlternateUrl();
    if (!url) {
      return;
    }

    const cached = getCachedEntry(url);
    if (cached) {
      fillPendingAgotadosCount(cached.html);
      return;
    }

    if (inFlight.has(url)) {
      return;
    }

    inFlight.add(url);

    fetch(url, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Availability prefetch failed (${response.status})`);
        }
        return response.text();
      })
      .then((html) => {
        if (!getCachedEntry(url)) {
          FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        }
        fillPendingAgotadosCount(html);
      })
      .catch((error) => {
        console.warn('[Promusic] Availability prefetch skipped:', error);
      })
      .finally(() => {
        inFlight.delete(url);
      });
  }

  class PmFacetsPrefetchScope extends HTMLElement {
    connectedCallback() {
      runAfterPageLoad(prefetchAlternateAvailability);
    }
  }

  if (!customElements.get('pm-facets-prefetch-scope')) {
    customElements.define('pm-facets-prefetch-scope', PmFacetsPrefetchScope);
  }
})();
